import type { Theme } from '@mui/material';
import { useTheme } from '@mui/material';
import type { TFunction } from 'i18next';
import type React from 'react';
import { useContext } from 'react';
import { useTranslation } from 'react-i18next';
import {
  D2N_SUB_ID,
  G2T_SUB_ID,
  KTS_SUB_ID,
  P2N_SUB_ID,
  S2N_SUB_ID,
  UNIVERSE_MONTHLY_SUB_ID,
  UNIVERSE_YEARLY_SUB_ID,
} from '../../../constants';
import { AppConstantsContext } from '../../../context';
import { JobType } from '../../../entities';

export interface SaleEventProps {
  name: string;
  discountData: {
    discount: number;
    discountedSubscriptions: string[];
    code: string;
  };
  title?: string;
  subtitle?: string;
  borderText?: string;
  navigationText?: string;
  backgroundAnimation?: React.ReactNode;
  styles?:
    | {
        title?: React.CSSProperties;
        subtitle?: React.CSSProperties;
        defaultStyles?: React.CSSProperties;
        card?: React.CSSProperties;
        button?: any;
        radioContainer?: React.CSSProperties;
        radioBorderText?: React.CSSProperties;
        price?: React.CSSProperties;
        bestValue?: React.CSSProperties;
        navigationButton?: any;
        '@global'?: any;
        '&::before'?: any;
        '&::after'?: any;
      }
    | any;
}

export const showEventByDate = (startDate: Date, endDate: Date): boolean => {
  const curDate = new Date();
  return (
    (startDate <= curDate && curDate <= endDate)
    || (typeof window !== 'undefined' && window.location?.search?.includes('test'))
  );
};

const getEarlyBirdSaleEvent = (_theme: Theme, t: TFunction): SaleEventProps => ({
  name: 'Early Bird',
  discountData: {
    discount: 50,
    discountedSubscriptions: [KTS_SUB_ID.MONTHLY, KTS_SUB_ID.YEARLY],
    code: 'EARLYBIRD',
  },
  title: t('home.pricing.special_event_title_kts'),
  subtitle: t('home.pricing.special_event_subtitle_kts'),
  borderText: t('home.pricing.special_event_border_text_kts'),
  navigationText: t('home.pricing.special_event_navigation_kts'),
  styles: {
    title: {
      fontFamily: 'DynaPuff',
      color: '#0ef7ff',
      fontWeight: 600,
      fontSize: '3rem',
      marginBottom: 0,
    },
    subtitle: {
      fontFamily: 'DynaPuff',
      fontSize: '1.1rem',
      fontWeight: 400,
      textAlign: 'center',
    },
    card: {
      boxShadow: `0px 0px 10px 10px #0ef7ff`,
    },
    button: {
      fontFamily: 'DynaPuff',
    },
    radioContainer: {
      border: `2px solid #0ef7ff`,
      margin: '10px 0',
    },
    radioBorderText: {
      fontSize: '.8rem',
      fontWeight: 600,
      fontFamily: 'DynaPuff',
      color: '#0ef7ff',
      backgroundColor: '#303031',
    },
    price: {
      color: '#0ef7ff',
      fontFamily: 'DynaPuff',
    },
    bestValue: {
      color: '#0ef7ff',
    },
    navigationButton: {
      background: '#0ef7ff',
      color: 'black',
    },
  },
});

// const getSpringSale = (_theme: Theme, t: TFunction): SaleEventProps => ({
//   name: 'Spring Sale',
//   discountData: {
//     discount: 40,
//     discountedSubscriptions: [
//       G2T_SUB_ID.MONTHLY,
//       G2T_SUB_ID.YEARLY,
//       P2N_SUB_ID.MONTHLY,
//       P2N_SUB_ID.YEARLY,
//       S2N_SUB_ID.MONTHLY,
//       S2N_SUB_ID.YEARLY,
//       D2N_SUB_ID.MONTHLY,
//       D2N_SUB_ID.YEARLY,
//       UNIVERSE_MONTHLY_SUB_ID,
//       UNIVERSE_YEARLY_SUB_ID,
//     ],
//     code: 'SPRING2025',
//   },
//   title: t('home.pricing.special_event_title_spring_sale'),
//   subtitle: t('home.pricing.special_event_subtitle_spring_sale'),
//   borderText: t('home.pricing.special_event_border_text_spring_sale'),
//   navigationText: t('home.pricing.special_event_navigation_spring_sale'),
//   styles: {
//     title: {
//       color: '#fff598',
//       fontWeight: 300,
//       fontSize: '4rem',
//       marginBottom: '.5em',
//       fontFamily: 'Pacifico',
//     },
//     subtitle: {
//       fontSize: '1.4rem',
//       textAlign: 'center',
//       fontFamily: 'Pacifico',
//     },
//     card: {
//       boxShadow: `0px 0px 10px 10px #fff598`,
//     },
//     button: {
//       backgroundColor: '#fff598',
//       color: '#000',
//       border: 'none',
//       ':hover': {
//         backgroundColor: '#ffd260',
//         color: '#000',
//         border: 'none',
//       },
//     },
//     radioContainer: {
//       border: `2px solid #fff598`,
//       margin: '10px 0',
//     },
//     radioBorderText: {
//       fontSize: '.9rem',
//       fontWeight: 400,
//       color: '#fff598',
//       backgroundColor: '#303031',
//       fontFamily: 'Pacifico',
//     },
//     price: {
//       color: '#fff598',
//       fontWeight: 300,
//       marginTop: '-10px',
//       fontSize: '2.2rem',
//       fontFamily: 'Pacifico',
//     },
//     bestValue: {
//       color: '#fff598',
//     },
//     navigationButton: {
//       backgroundColor: '#fff598',
//       border: 'none',
//       fontWeight: 600,
//       color: '#ec3057',
//       ':hover': {
//         backgroundColor: '#ffd260',
//         border: 'none',
//       },
//     },
//   },
// });

const scalerKeyframes = {
  '@keyframes scaleburst': {
    '0%': { transform: 'scale(1)' },
    '10%': { transform: 'scale(1.3)' },
    '20%': { transform: 'scale(0.9)' },
    '30%': { transform: 'scale(1.3)' },
    '40%': { transform: 'scale(0.9)' },
    '50%': { transform: 'scale(1.3)' },
    '60%': { transform: 'scale(1)' },
    '100%': { transform: 'scale(1)' },
  },
};

const getHeatwaveSale = (_theme: Theme, t: TFunction): SaleEventProps => ({
  name: 'Heatwave Sale',
  discountData: {
    discount: 50,
    discountedSubscriptions: [
      G2T_SUB_ID.YEARLY,
      P2N_SUB_ID.YEARLY,
      S2N_SUB_ID.YEARLY,
      D2N_SUB_ID.YEARLY,
      G2T_SUB_ID.MONTHLY,
      P2N_SUB_ID.MONTHLY,
      S2N_SUB_ID.MONTHLY,
      D2N_SUB_ID.MONTHLY,
      UNIVERSE_YEARLY_SUB_ID,
      UNIVERSE_MONTHLY_SUB_ID,
    ],
    code: 'HEATWAVE',
  },
  title: t('home.pricing.special_event_title_sale'),
  subtitle: t('home.pricing.special_event_subtitle_sale'),
  borderText: t('home.pricing.special_event_border_text_sale'),
  navigationText: t('home.pricing.special_event_navigation_sale'),
  styles: {
    title: {
      color: '#EBC332',
      fontSize: '4rem',
      marginBottom: '0',
      marginTop: '.6em',
      fontFamily: 'Bangers',
      background: 'linear-gradient(to top, #700000, #F80000, #ffc800, #f9e945)',
      WebkitBackgroundClip: 'text',
      WebkitTextFillColor: 'transparent',
      position: 'relative',
      animation: 'scaleburst 9.5s ease-in-out infinite',
      ...scalerKeyframes,

      '&::before': {
        content: '"☀️"',
        fontSize: '3.5rem',
        padding: '0 3px',
        color: '#EBC332',
        WebkitTextFillColor: 'white',
      },

      '&::after': {
        content: '"☀️"',
        fontSize: '3.5rem',
        padding: '0 8px',
        color: 'EBC332',
        WebkitTextFillColor: 'white',
      },
    },
    subtitle: {
      fontSize: '1.4rem',
      textAlign: 'center',
      fontFamily: 'Poppins',
    },
    card: {
      boxShadow:
        '0px 0px 10px 2px rgb(255 232 0), 0px 0px 20px 6px rgb(255 0 0), 0px 0px 30px 12px rgba(255, 0, 0, 0.1)',
    },
    button: {
      background: '#F80000',
      border: 'none',
      color: '#fff',
      ':hover': {
        opacity: 0.8,
        background: '#F80000',
        color: '#fff',
        border: 'none',
        animation: 'sheen 1s ease-in-out',
      },
    },
    radioContainer: {
      border: `2px solid #EBC332`,
      margin: '10px 0',
    },
    radioBorderText: {
      fontSize: '1.3rem',
      fontWeight: 400,
      top: '-19px',
      color: '#EBC332',
      backgroundColor: '#303031',
      fontFamily: 'Bangers',
    },
    price: {
      color: '#EBC332',
      fontSize: '2.2rem',
      fontWeight: 'lighter',
      fontFamily: 'Bangers',
    },
    bestValue: {
      color: '#EBC332',
      fontFamily: 'Bangers',
      fontSize: '1.2rem',
      fontWeight: 'lighter',
    },
    navigationButton: {
      color: '#fff',
      border: 'none',
      fontWeight: 600,
      background: '#2e2d2d',
      boxShadow:
        '0px 0px 4px 2px rgb(255 232 0), 0px 0px 13px 3px rgb(255 0 0), 0px 0px 30px 12px rgba(255, 0, 0, 0.1)',
      ':hover': {
        background: '#2e2d2d',
        boxShadow:
          '0px 0px 4px 2px rgb(255 232 0), 0px 0px 13px 3px rgb(255 0 0), 0px 0px 30px 12px rgba(255, 0, 0, 0.1)',
        border: 'none',
        filter: 'brightness(1.2)',
      },
    },
  },
});

const useSaleEvent = () => {
  const { jobType, subscriptionIds, isMusehub } = useContext(AppConstantsContext);
  const theme = useTheme();
  const { t } = useTranslation();
  let saleEvent: SaleEventProps | null = null;
  if (isMusehub) {
    saleEvent = null;
  } else if (jobType === JobType.TranscribeStudio) {
    saleEvent = getEarlyBirdSaleEvent(theme, t);
  } else if (showEventByDate(new Date('2025-07-03 12:00'), new Date('2025-07-14 12:00'))) {
    saleEvent = getHeatwaveSale(theme, t);
    if (
      !Object.values(subscriptionIds).find((subscriptionId) =>
        saleEvent?.discountData.discountedSubscriptions.includes(subscriptionId))
    ) {
      saleEvent = null;
    }
  }

  return { saleEvent };
};

export default useSaleEvent;
